import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Download as DownloadIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { db } from '../../firebase'
import {
  ADMIN_MESSAGES_COLLECTION,
  MAX_STORED_RECIPIENTS,
  type AdminMessageLogDoc,
  type AdminMessageRecipient,
  type AdminMessageStatus,
  type AdminMessageType,
} from '../../services/adminMessageLogService'
import { exportToExcel } from '../../services/exportService'

type StatusFilter = AdminMessageStatus | 'all'
type TypeFilter = AdminMessageType | 'all'

interface MessageLogRow extends AdminMessageLogDoc {
  id: string
}

const statusMeta: Record<AdminMessageStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  sending: { label: 'جارٍ الإرسال', color: 'info' },
  sent: { label: 'تم الإرسال', color: 'success' },
  partial: { label: 'إرسال جزئي', color: 'warning' },
  failed: { label: 'فشل الإرسال', color: 'error' },
}

const typeMeta: Record<AdminMessageType, string> = {
  notification: 'إشعار داخل التطبيق',
  email: 'رسالة بريد إلكتروني',
}

const userTypeLabels: Record<string, string> = {
  buyer: 'مشتري',
  seller: 'تاجر',
  admin: 'مدير',
}

function formatDateTime(value: any): string {
  if (!value) return '—'
  try {
    const date = value?.toDate ? value.toDate() : new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

function formatFilters(row: MessageLogRow): string {
  const parts: string[] = []
  const type = row.filters?.type
  if (type && type !== 'all') parts.push(type === 'seller' ? 'التجار' : type === 'buyer' ? 'المشترون' : 'المديرون')
  const status = row.filters?.status
  if (status && status !== 'all') parts.push(status === 'active' ? 'نشط' : 'غير نشط')
  if (row.filters?.categoryNames?.length) {
    parts.push(`الفئات: ${row.filters.categoryNames.join('، ')}`)
  }
  if (row.filters?.search) parts.push(`بحث: ${row.filters.search}`)
  if (row.filters?.includeAllowedCategories) parts.push('شمل الفئات المسموح بها فقط')
  return parts.length ? parts.join(' • ') : 'بدون فلاتر'
}

export default function MessageHistoryPage() {
  const [rows, setRows] = useState<MessageLogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [details, setDetails] = useState<MessageLogRow | null>(null)
  const [recipientSearch, setRecipientSearch] = useState('')
  const [recipientStatusFilter, setRecipientStatusFilter] = useState<'all' | 'sent' | 'failed'>('all')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' })

  /** أقصى عدد سجلات محمّلة — السجل قد ينمو، فنحدّث الاستعلام عند الوصول للحد */
  const [queryLimit, setQueryLimit] = useState(200)

  useEffect(() => {
    setLoading(true)
    setLoadError(null)
    const q = query(
      collection(db, ADMIN_MESSAGES_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(queryLimit)
    )
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })))
        setLoading(false)
      },
      (error) => {
        console.error('فشل تحميل سجل الرسائل:', error)
        setLoadError(
          'تعذّر قراءة سجل الرسائل. تأكد من صلاحيات الأدمن ومن نشر قواعد Firestore.'
        )
        setLoading(false)
      }
    )
    return () => unsub()
  }, [queryLimit])

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return rows.filter((row) => {
      const matchesStatus = statusFilter === 'all' || row.status === statusFilter
      const matchesType = typeFilter === 'all' || row.messageType === typeFilter
      if (!matchesStatus || !matchesType) return false
      if (!q) return true
      return (
        (row.title || '').toLowerCase().includes(q) ||
        (row.body || '').toLowerCase().includes(q) ||
        (row.adminEmail || '').toLowerCase().includes(q) ||
        (row.recipients || []).some(
          (r) =>
            (r.name || '').toLowerCase().includes(q) ||
            (r.phone || '').includes(q) ||
            (r.userId || '').toLowerCase().includes(q)
        )
      )
    })
  }, [rows, searchQuery, statusFilter, typeFilter])

  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))

  const totals = useMemo(() => {
    const delivered = filteredRows.reduce(
      (acc, r) => acc + (typeof r.successCount === 'number' ? r.successCount : (r.recipients || []).filter((x) => x.status === 'sent').length),
      0
    )
    return {
      messages: filteredRows.length,
      delivered,
      failed: filteredRows.reduce(
        (acc, r) => acc + (typeof r.failedCount === 'number' ? r.failedCount : 0),
        0
      ),
    }
  }, [filteredRows])

  const visibleRecipients: AdminMessageRecipient[] = useMemo(() => {
    if (!details) return []
    const list = details.recipients || []
    const q = recipientSearch.trim().toLowerCase()
    return list.filter((r) => {
      const matchesStatus =
        recipientStatusFilter === 'all' || r.status === recipientStatusFilter
      if (!matchesStatus) return false
      if (!q) return true
      return (
        (r.name || '').toLowerCase().includes(q) ||
        (r.phone || '').includes(q) ||
        (r.userId || '').toLowerCase().includes(q)
      )
    })
  }, [details, recipientSearch, recipientStatusFilter])

  const handleExport = () => {
    try {
      const data = filteredRows.map((row) => ({
        'التاريخ': formatDateTime(row.createdAt),
        'العنوان': row.title || '',
        'النوع': typeMeta[row.messageType] || row.messageType,
        'الحالة': statusMeta[row.status]?.label || row.status,
        'المرسل': row.adminEmail || row.adminId || '',
        'عدد المستلمين': row.recipientCount ?? 0,
        'تم الإرسال': row.successCount ?? 0,
        'فشل': row.failedCount ?? 0,
        'الفلاتر': formatFilters(row),
        'المحتوى': row.body || '',
      }))
      exportToExcel(data, 'سجل_الرسائل', 'سجل الرسائل')
      setSnackbar({ open: true, message: 'تم تصدير السجل بنجاح', severity: 'success' })
    } catch (error) {
      console.error('خطأ في تصدير سجل الرسائل:', error)
      setSnackbar({ open: true, message: 'حدث خطأ أثناء التصدير', severity: 'error' })
    }
  }

  const handleExportRecipients = () => {
    if (!details) return
    try {
      const data = (details.recipients || []).map((r) => ({
        'الاسم': r.name || '',
        'رقم الهاتف': r.phone || '',
        'النوع': userTypeLabels[r.userType] || r.userType || '',
        'حالة الإرسال': r.status === 'sent' ? 'تم' : `فشل${r.error ? ` — ${r.error}` : ''}`,
        'معرف المستخدم': r.userId || '',
      }))
      exportToExcel(data, `مستلمي_${details.title || 'رسالة'}`, 'المستلمون')
      setSnackbar({ open: true, message: 'تم تصدير قائمة المستلمين بنجاح', severity: 'success' })
    } catch (error) {
      console.error('خطأ في تصدير المستلمين:', error)
      setSnackbar({ open: true, message: 'حدث خطأ أثناء التصدير', severity: 'error' })
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
        سجل رسائل الإدارة
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        كل رسالة يرسلها الأدمن للمستخدمين تُسجَّل هنا: من أرسلها، متى، نصها، الفلاتر المستخدمة،
        ومن استلمها — مع نتيجة الإرسال لكل مستخدم.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{totals.messages}</Typography>
              <Typography variant="body2">رسالة ضمن النتائج</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{totals.delivered}</Typography>
              <Typography variant="body2">إشعار تم تسليمه</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{totals.failed}</Typography>
              <Typography variant="body2">إرسال فاشل</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="ابحث في العنوان أو المحتوى أو اسم/هاتف المستلم..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>نوع الرسالة</InputLabel>
                <Select
                  value={typeFilter}
                  label="نوع الرسالة"
                  onChange={(e) => {
                    setTypeFilter(e.target.value as TypeFilter)
                    setPage(1)
                  }}
                >
                  <MenuItem value="all">كل الأنواع</MenuItem>
                  <MenuItem value="notification">إشعار داخل التطبيق</MenuItem>
                  <MenuItem value="email">رسالة بريد إلكتروني</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>حالة الإرسال</InputLabel>
                <Select
                  value={statusFilter}
                  label="حالة الإرسال"
                  onChange={(e) => {
                    setStatusFilter(e.target.value as StatusFilter)
                    setPage(1)
                  }}
                >
                  <MenuItem value="all">كل الحالات</MenuItem>
                  <MenuItem value="sent">تم الإرسال</MenuItem>
                  <MenuItem value="partial">إرسال جزئي</MenuItem>
                  <MenuItem value="failed">فشل الإرسال</MenuItem>
                  <MenuItem value="sending">جارٍ الإرسال</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="contained" startIcon={<DownloadIcon />} onClick={handleExport}>
                  تصدير
                </Button>
                <Tooltip title="تحميل رسائل أقدم">
                  <IconButton
                    onClick={() => setQueryLimit((l) => l + 200)}
                    disabled={rows.length < queryLimit}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
          {rows.length >= queryLimit && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              يتم عرض أحدث {queryLimit} رسالة. اضغط زر التحديث لعرض رسائل أقدم.
            </Typography>
          )}
        </CardContent>
      </Card>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {loading && rows.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !loadError && rows.length === 0 && (
        <Alert severity="info" icon={<HistoryIcon fontSize="inherit" />}>
          لا توجد رسائل مسجّلة بعد. عند إرسال رسالة من صفحة «المستخدمون» ستظهر هنا تلقائياً.
        </Alert>
      )}

      {rows.length > 0 && filteredRows.length === 0 && (
        <Alert severity="warning">لا توجد نتائج مطابقة للفلاتر المحددة.</Alert>
      )}

      {filteredRows.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            mb: 3,
            width: '100%',
            overflowX: 'auto',
            borderRadius: { xs: 2, md: 3 },
            boxShadow: { xs: 0, md: 1 },
          }}
        >
          <Table size="small" sx={{ minWidth: { xs: 900, md: '100%' } }}>
            <TableHead>
              <TableRow>
                <TableCell><strong>التاريخ</strong></TableCell>
                <TableCell><strong>العنوان</strong></TableCell>
                <TableCell><strong>النوع</strong></TableCell>
                <TableCell><strong>المرسل</strong></TableCell>
                <TableCell align="center"><strong>المستلمون</strong></TableCell>
                <TableCell align="center"><strong>تم / فشل</strong></TableCell>
                <TableCell><strong>الحالة</strong></TableCell>
                <TableCell><strong>التفاصيل</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row) => {
                const meta = statusMeta[row.status] || statusMeta.sent
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.title || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFilters(row)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={typeMeta[row.messageType] || row.messageType}
                      />
                    </TableCell>
                    <TableCell>{row.adminEmail || row.adminId || '—'}</TableCell>
                    <TableCell align="center">{row.recipientCount ?? 0}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="success.main">
                        {row.successCount ?? 0}
                      </Typography>
                      <Typography variant="body2" color="error.main">
                        {row.failedCount ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={meta.label} color={meta.color} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="عرض التفاصيل والمستلمين">
                        <IconButton size="small" onClick={() => {
                          setRecipientSearch('')
                          setRecipientStatusFilter('all')
                          setDetails(row)
                        }}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
          <Pagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="large"
          />
        </Stack>
      )}

      {/* تفاصيل الرسالة والمستلمون */}
      <Dialog open={!!details} onClose={() => setDetails(null)} fullWidth maxWidth="md">
        <DialogTitle>تفاصيل الرسالة</DialogTitle>
        <DialogContent>
          {details && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {details.title}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {details.body}
                </Typography>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">تاريخ الإرسال: </Typography>
                  <Typography variant="body2">{formatDateTime(details.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">أنهى الإرسال: </Typography>
                  <Typography variant="body2">{formatDateTime(details.completedAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">أرسلها: </Typography>
                  <Typography variant="body2">{details.adminEmail || details.adminId || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">المصدر: </Typography>
                  <Typography variant="body2">
                    {details.source === 'users_page' ? 'صفحة المستخدمين' : details.source || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">نوع الرسالة: </Typography>
                  <Typography variant="body2">
                    {typeMeta[details.messageType] || details.messageType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">الفلاتر وقت الإرسال: </Typography>
                  <Typography variant="body2">{formatFilters(details)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">عدد المستلمين: </Typography>
                  <Typography variant="body2">
                    {details.recipientCount ?? 0} (تم: {details.successCount ?? 0} • فشل: {details.failedCount ?? 0})
                  </Typography>
                </Grid>
              </Grid>

              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2" fontWeight="bold">
                    قائمة المستلمين
                  </Typography>
                  <Chip size="small" label={`${(details.recipients || []).length} معروض`} />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportRecipients}>
                    تصدير المستلمين
                  </Button>
                </Stack>
                {details.recipientsTruncated && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    تم حفظ تفاصيل أول {MAX_STORED_RECIPIENTS} مستلم فقط داخل هذا السجل، بينما العدد
                    الإجمالي {details.recipientCount} — راجع أيضاً إشعارات المستخدمين للتأكد من
                    الاستلام.
                  </Alert>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="ابحث بالاسم أو الهاتف أو المعرف..."
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    sx={{ minWidth: 220, flex: '1 1 260px' }}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>الحالة</InputLabel>
                    <Select
                      label="الحالة"
                      value={recipientStatusFilter}
                      onChange={(e) => setRecipientStatusFilter(e.target.value as 'all' | 'sent' | 'failed')}
                    >
                      <MenuItem value="all">الكل</MenuItem>
                      <MenuItem value="sent">تم الإرسال</MenuItem>
                      <MenuItem value="failed">فشل</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <TableContainer sx={{ mt: 1, maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>الاسم</strong></TableCell>
                        <TableCell><strong>رقم الهاتف</strong></TableCell>
                        <TableCell><strong>النوع</strong></TableCell>
                        <TableCell><strong>الحالة</strong></TableCell>
                        <TableCell><strong>التفاصيل</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRecipients.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="caption" color="text.secondary">
                              لا يوجد مستلمون مطابقون.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {visibleRecipients.map((r, i) => (
                        <TableRow key={`${r.userId}-${i}`}>
                          <TableCell>{r.name || '—'}</TableCell>
                          <TableCell>{r.phone || '—'}</TableCell>
                          <TableCell>{userTypeLabels[r.userType] || r.userType || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.status === 'sent' ? 'تم' : 'فشل'}
                              color={r.status === 'sent' ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {r.error || r.userId}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetails(null)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
